import React from 'react';
import { CheckIcon, DownloadIcon, ShieldCheckIcon, StarIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { HERO_IMAGE, REGULAR_PRICE, SALE_PRICE, standards } from '../data/content';
import { HERO_SIZES, HERO_WIDTHS, optimizedImage, optimizedSrcSet } from '../utils/images';
const proofPoints = ['Instant access', 'Lifetime updates', 'Secure checkout'];

/**
 * Everything here is above the fold, so it renders immediately with no entrance
 * animation — the mockup is the LCP element and must paint as fast as possible.
 */
export function Hero() {
  return <header className="relative w-full overflow-hidden border-b border-white/5 bg-steel-950" data-id="element-89" data-mp="sb1a5lxo1t4g">
      <div className="blueprint-grid absolute inset-0" aria-hidden="true" data-id="element-90" data-mp="nc858hwg3afw" />
      <div className="absolute inset-x-0 top-0 h-px bg-ember-500" aria-hidden="true" data-id="element-91" data-mp="neauv0tor1k3" />

      <div className="relative mx-auto flex max-w-4xl flex-col items-center px-6 pb-8 pt-5 text-center sm:pt-7" data-id="element-92" data-mp="3d2tqbf4z2uh6">
        <p className="inline-flex items-center border border-ember-500/40 bg-ember-500/10 px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-ember-300 sm:text-[0.68rem]" data-id="element-93" data-mp="384jq2q4e53op">
          Industrial Instrumentation Instructor System 2026
        </p>

        <h1 className="mt-3 font-display text-[2rem] font-bold uppercase leading-[1] tracking-tight text-ink-50 sm:text-5xl lg:text-6xl" data-id="element-94" data-mp="399jnddjryf64">
          Teach The Plant Floor.
          <span className="mt-0.5 block text-ember-400" data-id="element-95" data-mp="30hrgtdxvfq6t">Everything Ready.</span>
        </h1>

        <img src={optimizedImage(HERO_IMAGE, 640)} srcSet={optimizedSrcSet(HERO_IMAGE, HERO_WIDTHS)} sizes={HERO_SIZES} alt="The complete Industrial Instrumentation Instructor System 2026: six core volumes, six bonus guides and printable classroom worksheets" width={1000} height={1000} fetchPriority="high" decoding="async" className="mx-auto mb-1 mt-3 h-auto max-h-[34vh] w-full max-w-lg object-contain drop-shadow-[0_24px_50px_rgba(0,0,0,0.9)] sm:mb-2 sm:mt-4 sm:max-h-[38vh] sm:max-w-xl" data-id="element-96" data-mp="3599jq7jgg0uf" />

        <p className="max-w-xl text-sm leading-relaxed text-ink-200 sm:text-base" data-id="element-97" data-mp="nbl1chqpya8v">
          6 technical volumes + 6 instructor bonuses. Ready to teach tomorrow.
        </p>

        <ul className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-ink-400 sm:text-sm" data-id="element-98" data-mp="i9vpizvo9sjq">
          {proofPoints.map(item => <li key={item} className="flex items-center gap-1.5" data-id="element-99" data-mp="1b0drtyoknpw3">
              <CheckIcon className="h-3.5 w-3.5 text-circuit-400" aria-hidden="true" data-id="element-100" data-mp="161aul0eu2nyo" />
              {item}
            </li>)}
        </ul>

        <CtaButton className="mt-5" label="Get The Full System Now" source="hero" data-id="element-101" data-mp="110wqtuk9m06g" />

        <p className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 font-display text-xs uppercase tracking-[0.16em] text-ink-500" data-id="element-102" data-mp="x0l3ga0iplra">
          <span data-id="element-103" data-mp="3hrlp6vpyqvfb">
            <span className="line-through" data-id="element-104" data-mp="3hr3qlsftw548">${REGULAR_PRICE}</span>{' '}
            <span className="text-ink-200" data-id="element-105" data-mp="1x9b0yopm2f6r">${SALE_PRICE} today</span> · One-time payment
          </span>
          <span className="flex items-center gap-1.5 normal-case tracking-normal" data-id="element-106" data-mp="21amv97jfjola">
            <span className="flex" aria-hidden="true" data-id="element-107" data-mp="218k0693f759p">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="h-3.5 w-3.5 fill-ember-400 text-ember-400" data-id="element-108" data-mp="2b0pmb26zqsgz" />)}
            </span>
            4.9/5
          </span>
          <span className="flex items-center gap-1.5 normal-case tracking-normal" data-id="element-109" data-mp="2fzaov89pto8l">
            <ShieldCheckIcon className="h-3.5 w-3.5 text-circuit-400" aria-hidden="true" data-id="element-110" data-mp="2kxuebinipi5p" />
            7-day guarantee
          </span>
          <span className="flex items-center gap-1.5 normal-case tracking-normal" data-id="element-111" data-mp="2oye7kb4z01mk">
            <DownloadIcon className="h-3.5 w-3.5 text-circuit-400" aria-hidden="true" data-id="element-112" data-mp="2tz1zcz10wmhu" />
            Instant download
          </span>
        </p>
      </div>

      <div className="relative border-t border-white/5 bg-steel-900/60" data-id="element-113" data-mp="zl11agdas3it">
        <ul className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-2 px-6 py-3" data-id="element-114" data-mp="zndop91tr639">
          {standards.map(({
          code,
          label
        }) => <li key={code} className="flex items-baseline gap-2" data-id="element-115" data-mp="3vh5leasbre9b">
              <span className="font-display text-base font-semibold uppercase tracking-wider text-ink-50" data-id="element-116" data-mp="e9tqnqklxx1t">
                {code}
              </span>
              <span className="text-xs uppercase tracking-[0.14em] text-ink-500" data-id="element-117" data-mp="98e41u2s7ihb">{label}</span>
            </li>)}
        </ul>
      </div>
    </header>;
}